export type OrderProfile =
  | 'general'
  | 'service'
  | 'internal'
  | 'sales'
  | 'rental'
  | 'spare-parts'
  | 'accessories'
  | 'trade-in';

export type OrderStatus =
  | 'draft'
  | 'new'
  | 'planned'
  | 'assigned'
  | 'in-progress'
  | 'reported'
  | 'billing-ready'
  | 'billed'
  | 'completed'
  | 'cancelled';

export type OrderListBucket = 'new' | 'in-progress' | 'completed' | 'all';
export type OrderAddressKind = 'billing' | 'delivery' | 'site';
export type OrderPositionKind =
  | 'machine'
  | 'article'
  | 'service'
  | 'labor'
  | 'travel'
  | 'rental'
  | 'transport'
  | 'text';

export interface OrderCustomerOption {
  readonly id: string;
  readonly number: string;
  readonly displayName: string;
  readonly contactName: string;
  readonly email: string;
  readonly phone: string;
  readonly billingAddress: OrderAddressInput;
  readonly deliveryAddress: OrderAddressInput | null;
}

/** Schlanker Entwurf für das Inline-Anlegen eines Kunden direkt im Vorgang (Vorgang-Zentrierung). */
export interface NewCustomerDraft {
  readonly kind: 'company' | 'person';
  readonly companyName: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly street: string;
  readonly postalCode: string;
  readonly city: string;
  readonly phone: string;
  readonly email: string;
}

export interface OrderMachineOption {
  readonly id: string;
  readonly number: string;
  readonly customerId: string | null;
  readonly manufacturer: string;
  readonly model: string;
  readonly serialNumber: string;
  readonly status: string;
}

export interface OrderItemOption {
  readonly id: string;
  readonly number: string;
  readonly kind: 'article' | 'service';
  readonly name: string;
  readonly unit: string;
  readonly salesPrice: number;
  readonly purchasePrice: number;
  readonly vatRate: number;
}

export interface OrderAddressInput {
  readonly kind: OrderAddressKind;
  readonly label: string;
  readonly companyName?: string;
  readonly contactName?: string;
  readonly street?: string;
  readonly postalCode?: string;
  readonly city?: string;
  readonly countryCode?: string;
}

export interface OrderAddressRecord extends OrderAddressInput {
  readonly id: string;
}

export interface OrderConditions {
  readonly customerReference: string;
  readonly paymentTermDays: number;
  readonly cashDiscountPercent: number;
  readonly cashDiscountDays: number;
  readonly orderDiscountPercent: number;
  readonly requestedDate: string | null;
  readonly deliveryMethod: string;
  readonly deliveryTerms: string;
  /** Belegdatum (ISO YYYY-MM-DD) oder null — ARISTA-Kopfdaten „Datum". */
  readonly documentDate: string | null;
  /** Belegsprache (z. B. „de" / „en") — ARISTA-Kopfdaten „Belegsprache". */
  readonly language: string;
  /** Kostenstelle (Masters-Eintrag-Id) oder null — ARISTA-Kopfdaten „Kostenstelle". */
  readonly costCenterId: string | null;
  /** Denormalisierter Kostenstellen-Name zum Belegzeitpunkt. */
  readonly costCenterName: string;
}

export interface OrderPositionInput {
  readonly kind: OrderPositionKind;
  readonly sourceId?: string | null;
  readonly sourceNumber?: string;
  readonly machineId?: string | null;
  readonly description: string;
  readonly quantity?: number;
  readonly unit?: string;
  readonly unitPrice?: number;
  readonly purchasePrice?: number;
  readonly discountPercent?: number;
  readonly vatRate?: number;
  readonly internalText?: string;
  readonly customerText?: string;
}

export interface OrderPositionRecord {
  readonly id: string;
  readonly positionNumber: number;
  readonly kind: OrderPositionKind;
  readonly sourceId: string | null;
  readonly sourceNumber: string;
  readonly machineId: string | null;
  readonly description: string;
  readonly quantity: number;
  readonly unit: string;
  readonly unitPrice: number;
  readonly purchasePrice: number;
  readonly discountPercent: number;
  readonly netAmount: number;
  readonly vatRate: number;
  readonly vatAmount: number;
  readonly grossAmount: number;
  readonly internalText: string;
  readonly customerText: string;
}

export interface OrderStatusHistoryRecord {
  readonly id: string;
  readonly fromStatus: OrderStatus | null;
  readonly toStatus: OrderStatus;
  readonly reason: string;
  readonly actor: string;
  readonly occurredAt: string;
}

export interface OrderRecord {
  readonly id: string;
  readonly tenantId: string;
  readonly number: string;
  readonly profile: OrderProfile;
  readonly title: string;
  readonly status: OrderStatus;
  readonly bucket: Exclude<OrderListBucket, 'all'>;
  readonly priority: 'low' | 'normal' | 'high' | 'urgent';
  readonly customerId: string | null;
  readonly customerNumber: string;
  readonly customerName: string;
  readonly contactName: string;
  readonly contactEmail: string;
  readonly contactPhone: string;
  readonly machineId: string | null;
  readonly machineNumber: string;
  readonly machineLabel: string;
  readonly assignedTo: string;
  readonly dueAt: string | null;
  readonly notes: string;
  readonly addresses: readonly OrderAddressRecord[];
  readonly conditions: OrderConditions;
  readonly positions: readonly OrderPositionRecord[];
  readonly subtotal: number;
  readonly positionDiscountTotal: number;
  readonly orderDiscountTotal: number;
  readonly netTotal: number;
  readonly vatTotal: number;
  readonly grossTotal: number;
  readonly statusHistory: readonly OrderStatusHistoryRecord[];
  readonly createdAt: string;
  readonly updatedAt: string;
}

export type OrderDocumentKind = 'offer' | 'confirmation' | 'delivery-note' | 'workshop-order';

/** Ein erzeugter Vorgangs-Beleg: eigene Nummer + Snapshot des Vorgangs zum Ausstellungszeitpunkt. */
export interface OrderDocumentRecord {
  readonly id: string;
  readonly orderId: string;
  readonly orderNumber: string;
  readonly kind: OrderDocumentKind;
  readonly number: string;
  readonly title: string;
  readonly createdBy: string;
  readonly createdAt: string;
  /** Der Vorgang, wie er beim Erstellen aussah -- daraus wird der Beleg gerendert. */
  readonly snapshot: OrderRecord;
}

export interface CreateOrderDocumentInput {
  readonly tenantId: string;
  readonly orderId: string;
  readonly kind: OrderDocumentKind;
}

export interface OrderOverview {
  readonly newCount: number;
  readonly inProgressCount: number;
  readonly completedCount: number;
  readonly billingReadyCount: number;
}

export interface OrderSearchInput {
  readonly tenantId: string;
  readonly query?: string;
  readonly bucket?: OrderListBucket;
  readonly profile?: OrderProfile | 'all';
  readonly page?: number;
  readonly pageSize?: number;
}

export interface NormalizedOrderSearchInput {
  readonly tenantId: string;
  readonly query: string;
  readonly bucket: OrderListBucket;
  readonly profile: OrderProfile | 'all';
  readonly page: number;
  readonly pageSize: number;
}

export interface OrderPage {
  readonly items: readonly OrderRecord[];
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

export interface SaveOrderInput {
  readonly tenantId: string;
  readonly orderId?: string;
  readonly profile: OrderProfile;
  readonly title: string;
  readonly customerId?: string | null;
  readonly contactName?: string;
  readonly contactEmail?: string;
  readonly contactPhone?: string;
  readonly machineId?: string | null;
  readonly priority?: 'low' | 'normal' | 'high' | 'urgent';
  readonly assignedTo?: string;
  readonly dueAt?: string | null;
  readonly notes?: string;
  readonly addresses: readonly OrderAddressInput[];
  readonly conditions: Partial<OrderConditions>;
  readonly positions: readonly OrderPositionInput[];
}

export interface TransitionOrderInput {
  readonly tenantId: string;
  readonly orderId: string;
  readonly toStatus: OrderStatus;
  readonly reason?: string;
}

export interface OrderGateway {
  getOverview(tenantId: string): Promise<OrderOverview>;
  searchOrders(input: OrderSearchInput): Promise<OrderPage>;
  getOrder(tenantId: string, orderId: string): Promise<OrderRecord>;
  searchCustomerOptions(tenantId: string, query?: string): Promise<readonly OrderCustomerOption[]>;
  /**
   * Maschinen-Optionen für den Vorgang. Ist `customerId` gesetzt (und `includeAll` false), werden nur
   * die Maschinen DIESES Kunden geliefert (für Serviceaufträge); `includeAll` zeigt alle Maschinen.
   */
  searchMachineOptions(tenantId: string, query?: string, customerId?: string | null, includeAll?: boolean): Promise<readonly OrderMachineOption[]>;
  searchItemOptions(tenantId: string, query?: string): Promise<readonly OrderItemOption[]>;
  saveOrder(input: SaveOrderInput): Promise<OrderRecord>;
  transitionOrder(input: TransitionOrderInput): Promise<OrderRecord>;
  createDocument(input: CreateOrderDocumentInput): Promise<OrderDocumentRecord>;
  listDocuments(tenantId: string, orderId: string): Promise<readonly OrderDocumentRecord[]>;
}

export interface OrderGridNavigationInput {
  readonly key: 'Enter' | 'Tab';
  readonly shiftKey: boolean;
  readonly rowIndex: number;
  readonly rowCount: number;
  readonly columnIndex: number;
  readonly columnCount: number;
}

export type OrderGridNavigationAction =
  | { readonly kind: 'previous-cell' }
  | { readonly kind: 'next-cell' }
  | { readonly kind: 'append-row' }
  | { readonly kind: 'stay' };

const profiles = new Set<OrderProfile>([
  'general', 'service', 'internal', 'sales', 'rental', 'spare-parts', 'accessories', 'trade-in',
]);
const positionKinds = new Set<OrderPositionKind>([
  'machine', 'article', 'service', 'labor', 'travel', 'rental', 'transport', 'text',
]);
const priorities = new Set(['low', 'normal', 'high', 'urgent']);

/**
 * The only Umsatzsteuer rates a position may carry. Exported so the UI offers exactly these and
 * cannot drift from what the validation accepts.
 */
export const germanVatRates: readonly number[] = [0, 7, 19];

/** Feste Kundennummer des eigenen Betriebs -- interne Aufträge laufen immer auf diesen Kunden. */
export const INTERNAL_CUSTOMER_NUMBER = '69999';

function money(value: number): number {
  return Math.round(value * 100) / 100;
}

function nonNegative(value: number | undefined, label: string): number {
  const normalized = value ?? 0;
  if (!Number.isFinite(normalized) || normalized < 0) throw new Error(`${label} darf nicht negativ sein.`);
  return money(normalized);
}

/**
 * The status graph for a Vorgang — the single source of truth shared by the demo gateway, the
 * Supabase adapter's guard and the FORQ Flow controls, so the allowed steps can never drift
 * between UI and backend (spec anti-pattern: no contradictory status definitions).
 */
export const orderTransitions: Readonly<Record<OrderStatus, readonly OrderStatus[]>> = {
  draft: ['new', 'cancelled'],
  new: ['planned', 'assigned', 'in-progress', 'cancelled'],
  planned: ['assigned', 'in-progress', 'cancelled'],
  assigned: ['in-progress', 'cancelled'],
  'in-progress': ['reported', 'billing-ready', 'cancelled'],
  reported: ['in-progress', 'billing-ready'],
  'billing-ready': ['billed', 'in-progress'],
  // GoBD-Reopen: Storno/Gutschrift einer versendeten Rechnung öffnet den Vorgang wieder zur Abrechnung.
  billed: ['completed', 'billing-ready'],
  completed: ['billing-ready'],
  cancelled: [],
};

/** The statuses a Vorgang in `status` may move to next. */
export function allowedTransitionsFor(status: OrderStatus): readonly OrderStatus[] {
  return orderTransitions[status];
}

export function orderBucketForStatus(status: OrderStatus): Exclude<OrderListBucket, 'all'> {
  if (['draft', 'new'].includes(status)) return 'new';
  if (['completed', 'cancelled'].includes(status)) return 'completed';
  return 'in-progress';
}

/**
 * Grobe ARISTA-Phase eines Vorgangs (Vorgang-Zentrierung, Etappe 3). Die zehn Feinstatus werden auf
 * wenige beleggetriebene Phasen gruppiert -- so folgt der sichtbare Status den Belegen statt einer
 * langen Klickstrecke. Alle Vorgänge laufen Offen → In Arbeit → Abgerechnet → Abgeschlossen (+ Storniert);
 * NUR Serviceaufträge behalten zusätzlich Geplant/Zugewiesen (für den Serviceplaner). Der zugrunde
 * liegende Feinstatus (inkl. reported/billing-ready) bleibt intern erhalten -- kein Datenumbau.
 */
export type OrderPhase = 'open' | 'planned' | 'assigned' | 'in-progress' | 'billed' | 'completed' | 'cancelled';

const statusPhase: Readonly<Record<OrderStatus, OrderPhase>> = {
  draft: 'open',
  new: 'open',
  planned: 'planned',
  assigned: 'assigned',
  'in-progress': 'in-progress',
  reported: 'in-progress',
  'billing-ready': 'in-progress',
  billed: 'billed',
  completed: 'completed',
  cancelled: 'cancelled',
};

export function orderPhaseOf(status: OrderStatus): OrderPhase {
  return statusPhase[status];
}

export const orderPhaseLabels: Readonly<Record<OrderPhase, string>> = {
  open: 'Offen',
  planned: 'Geplant',
  assigned: 'Zugewiesen',
  'in-progress': 'In Arbeit',
  billed: 'Abgerechnet',
  completed: 'Abgeschlossen',
  cancelled: 'Storniert',
};

/** Reihenfolge der Phasen für Fortschritt/Sortierung im Status-Trail. */
export const orderPhaseRank: Readonly<Record<OrderPhase, number>> = {
  open: 0, planned: 1, assigned: 2, 'in-progress': 3, billed: 4, completed: 5, cancelled: 6,
};

export function normalizeOrderSearchInput(input: OrderSearchInput): NormalizedOrderSearchInput {
  return {
    tenantId: input.tenantId,
    query: input.query?.trim() ?? '',
    bucket: input.bucket ?? 'new',
    profile: input.profile ?? 'all',
    page: Math.max(1, Math.trunc(input.page ?? 1)),
    pageSize: Math.min(100, Math.max(1, Math.trunc(input.pageSize ?? 25))),
  };
}

export function normalizeOrderAddress(input: OrderAddressInput): OrderAddressInput {
  const label = input.label.trim();
  if (!label) throw new Error('Eine Adresse benötigt eine Bezeichnung.');
  const countryCode = (input.countryCode?.trim().toUpperCase() || 'DE');
  if (!/^[A-Z]{2}$/.test(countryCode)) throw new Error('Der Ländercode muss aus zwei Buchstaben bestehen.');
  return {
    kind: input.kind,
    label,
    companyName: input.companyName?.trim() ?? '',
    contactName: input.contactName?.trim() ?? '',
    street: input.street?.trim() ?? '',
    postalCode: input.postalCode?.trim() ?? '',
    city: input.city?.trim() ?? '',
    countryCode,
  };
}

export function calculateOrderPosition(
  input: OrderPositionInput,
  positionNumber: number,
  id: string = crypto.randomUUID(),
): OrderPositionRecord {
  if (!positionKinds.has(input.kind)) throw new Error('Die Positionsart ist ungültig.');
  const description = input.description.trim();
  if (!description) throw new Error('Eine Position benötigt eine Bezeichnung.');
  const quantity = input.kind === 'text' ? 0 : nonNegative(input.quantity ?? 1, 'Die Menge');
  if (input.kind !== 'text' && quantity <= 0) throw new Error('Die Menge muss größer als null sein.');
  const unitPrice = input.kind === 'text' ? 0 : nonNegative(input.unitPrice, 'Der Einzelpreis');
  const purchasePrice = input.kind === 'text' ? 0 : nonNegative(input.purchasePrice, 'Der Einkaufspreis');
  const discountPercent = input.kind === 'text' ? 0 : nonNegative(input.discountPercent, 'Der Rabatt');
  const vatRate = input.kind === 'text' ? 0 : nonNegative(input.vatRate ?? 19, 'Der Umsatzsteuersatz');
  if (discountPercent > 100) throw new Error('Der Rabatt darf höchstens 100 Prozent betragen.');
  // Only the German rates. A free 0–100 field let a 12 % position exist, and an invoice built from
  // it could not be booked — the DATEV export would have to refuse it. Rejecting it here means the
  // bad state never reaches an invoice in the first place, on every entry path at once.
  if (!germanVatRates.includes(vatRate)) {
    throw new Error(`${vatRate} % ist kein gültiger Umsatzsteuersatz. Erlaubt sind 0, 7 oder 19 Prozent.`);
  }
  const grossBeforeDiscount = money(quantity * unitPrice);
  const netAmount = money(grossBeforeDiscount * (1 - discountPercent / 100));
  const vatAmount = money(netAmount * vatRate / 100);
  return {
    id,
    positionNumber,
    kind: input.kind,
    sourceId: input.sourceId ?? null,
    sourceNumber: input.sourceNumber?.trim() ?? '',
    machineId: input.machineId ?? null,
    description,
    quantity,
    unit: input.kind === 'text' ? '' : input.unit?.trim() || 'Stück',
    unitPrice,
    purchasePrice,
    discountPercent,
    netAmount,
    vatRate,
    vatAmount,
    grossAmount: money(netAmount + vatAmount),
    internalText: input.internalText?.trim() ?? '',
    customerText: input.customerText?.trim() ?? '',
  };
}

export function calculateOrderTotals(
  positions: readonly OrderPositionRecord[],
  orderDiscountPercent = 0,
): Pick<OrderRecord, 'subtotal' | 'positionDiscountTotal' | 'orderDiscountTotal' | 'netTotal' | 'vatTotal' | 'grossTotal'> {
  const subtotal = money(positions.reduce((sum, position) => (
    sum + position.quantity * position.unitPrice
  ), 0));
  const netBeforeOrderDiscount = money(positions.reduce((sum, position) => sum + position.netAmount, 0));
  const positionDiscountTotal = money(subtotal - netBeforeOrderDiscount);
  const safeOrderDiscount = Math.min(100, Math.max(0, orderDiscountPercent));
  const orderDiscountTotal = money(netBeforeOrderDiscount * safeOrderDiscount / 100);
  const netTotal = money(netBeforeOrderDiscount - orderDiscountTotal);
  const ratio = netBeforeOrderDiscount > 0 ? netTotal / netBeforeOrderDiscount : 0;
  const vatTotal = money(positions.reduce((sum, position) => sum + position.vatAmount * ratio, 0));
  return {
    subtotal,
    positionDiscountTotal,
    orderDiscountTotal,
    netTotal,
    vatTotal,
    grossTotal: money(netTotal + vatTotal),
  };
}

export function normalizeOrderInput(input: SaveOrderInput): SaveOrderInput {
  if (!profiles.has(input.profile)) throw new Error('Das Vorgangsprofil ist ungültig.');
  const title = input.title.trim();
  if (title.length < 3) throw new Error('Der Vorgang benötigt einen aussagekräftigen Titel.');
  const priority = input.priority ?? 'normal';
  if (!priorities.has(priority)) throw new Error('Die Priorität ist ungültig.');
  const addresses = input.addresses.map(normalizeOrderAddress);
  if (!addresses.some((address) => address.kind === 'billing')) {
    throw new Error('Der Vorgang benötigt eine Rechnungsadresse.');
  }
  const positions = input.positions
    .filter((position) => position.description.trim())
    .map((position) => ({ ...position, description: position.description.trim() }));
  if (positions.length === 0) throw new Error('Der Vorgang benötigt mindestens eine Position.');
  const paymentTermDays = Math.max(0, Math.trunc(input.conditions.paymentTermDays ?? 14));
  const cashDiscountPercent = nonNegative(input.conditions.cashDiscountPercent, 'Der Skontosatz');
  const cashDiscountDays = Math.max(0, Math.trunc(input.conditions.cashDiscountDays ?? 0));
  const orderDiscountPercent = nonNegative(input.conditions.orderDiscountPercent, 'Der Auftragsrabatt');
  if (cashDiscountPercent > 100 || orderDiscountPercent > 100) {
    throw new Error('Skonto und Auftragsrabatt dürfen höchstens 100 Prozent betragen.');
  }
  if (cashDiscountPercent > 0 && cashDiscountDays <= 0) {
    throw new Error('Für Skonto muss eine Skontofrist angegeben werden.');
  }
  return {
    tenantId: input.tenantId,
    ...(input.orderId ? { orderId: input.orderId } : {}),
    profile: input.profile,
    title,
    customerId: input.customerId ?? null,
    contactName: input.contactName?.trim() ?? '',
    contactEmail: input.contactEmail?.trim().toLowerCase() ?? '',
    contactPhone: input.contactPhone?.trim() ?? '',
    machineId: input.machineId ?? null,
    priority: priority as 'low' | 'normal' | 'high' | 'urgent',
    assignedTo: input.assignedTo?.trim() ?? '',
    dueAt: input.dueAt || null,
    notes: input.notes?.trim() ?? '',
    addresses,
    conditions: {
      customerReference: input.conditions.customerReference?.trim() ?? '',
      paymentTermDays,
      cashDiscountPercent,
      cashDiscountDays,
      orderDiscountPercent,
      requestedDate: input.conditions.requestedDate || null,
      deliveryMethod: input.conditions.deliveryMethod?.trim() ?? '',
      deliveryTerms: input.conditions.deliveryTerms?.trim() ?? '',
    },
    positions,
  };
}

export function resolveOrderGridNavigation(input: OrderGridNavigationInput): OrderGridNavigationAction {
  if (input.shiftKey) return { kind: 'previous-cell' };
  const isLastCell = input.rowIndex === input.rowCount - 1
    && input.columnIndex === input.columnCount - 1;
  if (isLastCell) return { kind: 'append-row' };
  if (input.key === 'Enter' || input.key === 'Tab') return { kind: 'next-cell' };
  return { kind: 'stay' };
}
